import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  Stack,
  useToast,
  Box,
  Flex,
  Text,
  Input,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Badge,
  MenuButton,
  Menu,
  MenuList,
  MenuItem,
  IconButton,
  Select,
  Tabs,
  TabList,
  Tab,
} from "@chakra-ui/react";
import { Loading } from "../../../components/Loading";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";
import { useNavigate } from "react-router-dom";
import { FiMoreVertical, FiSearch } from "react-icons/fi";
import { GoChevronLeft, GoChevronRight } from "react-icons/go";
import { getVerificationReviews, sendVerificationClarificationReminder } from "../../../actions/verificationReview.action";

const AwaitingClarification = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [entityFilter, setEntityFilter] = useState("all");
  const debounceTimer = useRef(null);

  const entityColorMap = {
    driver: "blue",
    vehicle: "purple",
    vendor: "orange",
  };

  const statusColorMap = {
    pending: "yellow",
    responded: "green",
    expired: "red",
  };

  const statusLabelMap = {
    pending: "Waiting for vendor",
    responded: "Resubmitted",
  };

  const detailsRoute = {
    driver: "/verification/driver-approvals",
    vehicle: "/verification/vehicle-approvals",
    vendor: "/verification/vendor-approvals",
  };

  const openDetails = (row) => {
    if (!row.entityId) return;
    navigate(`${detailsRoute[row.entityType]}/${row.entityId}`, {
      state: { row: { id: row.entityId, entityId: row.entityId, reviewId: row.id } },
    });
  };

  const formatDay = (value) => (value ? new Date(value).toISOString().split("T")[0] : "-");

  const loadData = useCallback(async () => {
    setFetching(true);
    try {
      const result = await getVerificationReviews({
        entityType: entityFilter === "all" ? "" : entityFilter,
        status: "awaiting_clarification", // waiting for vendor + resubmitted
        page: currentPage,
        limit: itemsPerPage,
        search: query,
      });

      const formattedData = (result.data || []).map(review => {
        const lastClarification = review.clarificationMessages?.[review.clarificationMessages.length - 1] || {};
        const fields = lastClarification.requestedFields || [];
        const fieldNames = fields.map((f) => f.fieldLabel || f.fieldKey).join(", ");
        const isResponded = review.status === "under_review" && !!lastClarification.respondedAt;
        return {
          id: review._id,
          entityId: review.entityId,
          entityType: review.entityType,
          entityName: review.displayName || review.displayCode || "Unknown",
          clarificationFor: fieldNames || lastClarification.message || "-",
          requestedAt: formatDay(lastClarification.sentAt),
          status: isResponded ? "responded" : "pending",
          respondedAt: formatDay(lastClarification.respondedAt),
          resubmittedCount: fields.filter((f) => f.isResubmitted).length,
          requestedCount: fields.length,
        };
      });

      setRawData(formattedData);
      setTotalPages(result.totalPages || 1);
    } catch (error) {
      toast({
        title: "Failed to fetch data!",
        description: error.message,
        status: "error",
        duration: 5000,
      });
    } finally {
      setFetching(false);
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, query, entityFilter, toast]);

  const sendReminder = async (row) => {
    try {
      await sendVerificationClarificationReminder({ reviewId: row.id });
      toast({
        title: "Reminder sent",
        description: `Sent reminder to ${row.entityName}`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Failed to send reminder",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setQuery(searchInput);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(debounceTimer.current);
  }, [searchInput]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const data = useMemo(() => {
    return rawData;
  }, [rawData]);

  const handleEntityChange = (entity) => {
    setEntityFilter(entity);
    setCurrentPage(1);
  };

  if (loading) return <Loading />;

  return (
    <Stack p={6} gap={4}>
      <BreadCrumbMaker
        items={[
          { link: "/verification", title: "Verification Center" },
          { link: "#", title: "Awaiting Clarification", on: true },
        ]}
      />

      <Box bg="white" borderRadius="xl" p={4} shadow="sm">
        <Flex justify="space-between" align="center" mb={4}>
          <Text fontSize="xl" fontWeight="bold" color="gray.700">
            Awaiting Clarification
          </Text>
          <Flex gap={2}>
            <Box position="relative">
              <Input
                placeholder="Search by name or clarification..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                pl={10}
                borderRadius="lg"
                w="300px"
              />
              <Box position="absolute" left={3} top="50%" transform="translateY(-50%)">
                <FiSearch color="gray" />
              </Box>
            </Box>
          </Flex>
        </Flex>

        <Tabs
          variant="soft-rounded"
          colorScheme="blue"
          mb={4}
          onChange={(index) => {
            const entities = ["all", "driver", "vehicle", "vendor"];
            handleEntityChange(entities[index]);
          }}
        >
          <TabList>
            <Tab>All</Tab>
            <Tab>Drivers</Tab>
            <Tab>Vehicles</Tab>
            <Tab>Vendors</Tab>
          </TabList>
        </Tabs>

        <Box overflowX="auto">
          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>Entity Type</Th>
                <Th>Name/Number</Th>
                <Th>Clarification For</Th>
                <Th>Requested At</Th>
                <Th>Responded At</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {fetching ? (
                <Tr>
                  <Td colSpan={7} textAlign="center" py={8}>
                    <Loading />
                  </Td>
                </Tr>
              ) : data.length === 0 ? (
                <Tr>
                  <Td colSpan={7} textAlign="center" py={8}>
                    <Text color="gray.500">No items awaiting clarification</Text>
                  </Td>
                </Tr>
              ) : (
                data.map((row) => (
                  <Tr key={row.id} _hover={{ bg: "gray.50" }}>
                    <Td>
                      <Badge colorScheme={entityColorMap[row.entityType]}>
                        {row.entityType.charAt(0).toUpperCase() + row.entityType.slice(1)}
                      </Badge>
                    </Td>
                    <Td fontWeight="medium">{row.entityName}</Td>
                    <Td>{row.clarificationFor}</Td>
                    <Td>{row.requestedAt}</Td>
                    <Td>{row.respondedAt}</Td>
                    <Td>
                      <Badge colorScheme={statusColorMap[row.status]}>
                        {statusLabelMap[row.status] || row.status}
                      </Badge>
                      {row.status === "responded" && row.requestedCount > 0 && (
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          {row.resubmittedCount}/{row.requestedCount} field(s) updated
                        </Text>
                      )}
                    </Td>
                    <Td>
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          icon={<FiMoreVertical />}
                          variant="ghost"
                          size="sm"
                        />
                        <MenuList>
                          <MenuItem onClick={() => openDetails(row)} isDisabled={!row.entityId}>
                            View Details
                          </MenuItem>
                          {row.status === "responded" && (
                            <>
                              {/* Decisions are taken on the details page, next to the resubmitted fields */}
                              <MenuItem color="green.500" onClick={() => openDetails(row)} isDisabled={!row.entityId}>
                                Approve
                              </MenuItem>
                              <MenuItem color="red.500" onClick={() => openDetails(row)} isDisabled={!row.entityId}>
                                Reject
                              </MenuItem>
                              <MenuItem color="orange.500" onClick={() => openDetails(row)} isDisabled={!row.entityId}>
                                Request Again
                              </MenuItem>
                            </>
                          )}
                          {row.status === "pending" && (
                            <MenuItem color="blue.500" onClick={() => sendReminder(row)}>Send Reminder</MenuItem>
                          )}
                        </MenuList>
                      </Menu>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>

        <Flex justify="space-between" align="center" mt={4}>
          <Flex align="center" gap={2}>
            <Text fontSize="sm" color="gray.600">Rows per page:</Text>
            <Select
              size="sm"
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              w="70px"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </Select>
          </Flex>
          <Flex align="center" gap={2}>
            <Text fontSize="sm" color="gray.600">
              Page {currentPage} of {totalPages}
            </Text>
            <IconButton
              icon={<GoChevronLeft />}
              size="sm"
              variant="outline"
              isDisabled={currentPage === 1}
              onClick={() => setCurrentPage(p => p - 1)}
            />
            <IconButton
              icon={<GoChevronRight />}
              size="sm"
              variant="outline"
              isDisabled={currentPage === totalPages}
              onClick={() => setCurrentPage(p => p + 1)}
            />
          </Flex>
        </Flex>
      </Box>
    </Stack>
  );
};

export default AwaitingClarification;
